import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useI18n } from '../i18n/i18nContext';
import LocalizedLink from '../components/LocalizedLink';
import LocalizedButton from '../components/Button/LocalizedButton';
import { axiosClient, getAssetUrl } from '../services/api';
import { Service } from '@shared/types';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { pushToDataLayer, formatServiceItem } from '../utils/dataLayer';
import SEO from '../components/SEO';

const MIME_TYPES: Record<string, string> = {
  mp4: 'video/mp4',
  webm: 'video/webm',
  ogv: 'video/ogg',
  mov: 'video/mp4',
  m4v: 'video/mp4',
  avi: 'video/x-msvideo',
  mkv: 'video/x-matroska',
};

function getVideoMimeType(url: string): string {
  const ext = url.split('?')[0].split('.').pop()?.toLowerCase() ?? '';
  return MIME_TYPES[ext] ?? 'video/mp4';
}

interface ExtendedService extends Service {
  slug: string;
  whatIs?: Record<string, string>;
  videoHeading?: Record<string, string>;
  videoUrl?: string;
  videoThumbnailUrl?: string;
  benefitsHeading?: Record<string, string>;
  benefits?: Record<string, string>;
  howItWorksHeading?: Record<string, string>;
  howItWorksBody?: Record<string, string>;
  experienceHeading?: Record<string, string>;
  experienceBody?: Record<string, string>;
  images?: string[];
}

const ServicePage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const { language, t } = useI18n();

  const [service, setService] = useState<ExtendedService | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const serviceName =
    (service?.[`name_${language}` as keyof Service] as string) || service?.name || '';
  // Append localized "in Tallinn" to service name for SEO
  const pageTitle = serviceName ? `${serviceName} ${t.common.inTallinn}` : '';
  useDocumentTitle(pageTitle);

  useEffect(() => {
    if (!slug) return;
    const fetchService = async () => {
      setLoading(true);
      setError(false);
      try {
        const response = await axiosClient.get<ExtendedService>(`/api/services/by-slug/${slug}`);
        setService(response.data);
      } catch (err) {
        console.error('Failed to fetch service:', err);
        setError(true);
      }
      setLoading(false);
    };
    fetchService();
  }, [slug]);

  useEffect(() => {
    if (service) {
      pushToDataLayer('view_item', {
        content: { category: 'booking_service' },
        ecommerce: {
          items: [formatServiceItem(service)],
        },
      });
    }
  }, [service]);

  const getText = (field: Record<string, string> | undefined) => {
    if (!field) return '';
    return field[language] || field['en'] || '';
  };

  const getYouTubeEmbedUrl = (url: string) => {
    const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]{11})/i);
    if (!match) return null;
    const id = match[1];
    return `https://www.youtube.com/embed/${id}?autoplay=1&mute=1&loop=1&playlist=${id}&controls=0&rel=0&playsinline=1`;
  };

  const getVimeoEmbedUrl = (url: string) => {
    const match = url.match(/vimeo\.com\/(\d+)/i);
    if (!match) return null;
    return `https://player.vimeo.com/video/${match[1]}?autoplay=1&muted=1&loop=1&background=1`;
  };

  const videoEmbedUrl = service?.videoUrl
    ? getYouTubeEmbedUrl(service.videoUrl) || getVimeoEmbedUrl(service.videoUrl)
    : null;
  const isDirectVideo = service?.videoUrl
    ? /\.(mp4|webm|ogv|mov|m4v|avi|mkv)(\?.*)?$/i.test(service.videoUrl) ||
      service.videoUrl.startsWith('/uploads/videos/')
    : false;
  const galleryImages = Array.isArray(service?.images)
    ? (service?.images ?? []).map((image) => (typeof image === 'string' ? image.trim() : ''))
    : [];
  const howItWorksImage = galleryImages[0] || '';
  const experienceImage = galleryImages[1] || '';
  const benefitsBackgroundImage = galleryImages[2] || '';

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-16">
        <div className="text-center text-gray-500">{t.common.loading}</div>
      </div>
    );
  }

  if (error || !service) {
    return (
      <div className="container mx-auto px-4 py-16">
        <div className="text-center">
          <h1 className="mb-4 text-2xl font-bold">Service not found</h1>
          <LocalizedLink to="/pricing" className="text-blue-600 hover:text-blue-700">
            {t.common.goHome}
          </LocalizedLink>
        </div>
      </div>
    );
  }

  // Get description safely based on slug
  const seoKey = slug as keyof typeof t.seo.services;
  const metaDescription = t.seo.services?.[seoKey] || t.seo.defaultDescription;

  return (
    <div>
      <SEO description={metaDescription} />
      <section
        className="relative overflow-hidden py-28 text-white md:py-56"
        style={{
          backgroundImage: service.videoUrl
            ? 'linear-gradient(to bottom, rgb(17, 24, 39), rgb(31, 41, 55))'
            : service.videoThumbnailUrl
              ? `url(${getAssetUrl(service.videoThumbnailUrl)})`
              : 'linear-gradient(to bottom, rgb(17, 24, 39), rgb(31, 41, 55))',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        {service.videoUrl && (
          <>
            {isDirectVideo ? (
              <video
                autoPlay
                muted
                loop
                playsInline
                poster={
                  service.videoThumbnailUrl ? getAssetUrl(service.videoThumbnailUrl) : undefined
                }
                className="absolute inset-0 z-0 h-full w-full object-cover"
              >
                <source
                  src={getAssetUrl(service.videoUrl)}
                  type={getVideoMimeType(service.videoUrl)}
                />
              </video>
            ) : videoEmbedUrl ? (
              <iframe
                src={videoEmbedUrl}
                title="Service video"
                className="absolute inset-0 z-0 h-full w-full"
                style={{ border: 0 }}
                allow="autoplay; encrypted-media"
                allowFullScreen={false}
              />
            ) : (
              service.videoThumbnailUrl && (
                <div
                  className="absolute inset-0 z-0"
                  style={{
                    backgroundImage: `url(${getAssetUrl(service.videoThumbnailUrl)})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                  }}
                />
              )
            )}
            <div className="absolute inset-0 z-10 bg-black/50"></div>
          </>
        )}
        {!service.videoUrl && service.videoThumbnailUrl && (
          <div className="absolute inset-0 z-10 bg-black/50"></div>
        )}
        <div className="container relative z-20 mx-auto px-4">
          <div className="mx-auto max-w-4xl text-center">
            <h1 className="mb-6 text-4xl font-bold md:text-5xl">{serviceName}</h1>
            <LocalizedButton to={`/book/${service.id}`} name={t.servicePage?.bookNow} size="md" />
          </div>
        </div>
      </section>

      <div className="container mx-auto max-w-6xl px-4 py-12">
        {getText(service.whatIs) && (
          <section className="mb-16 text-center">
            {getText(service.videoHeading) && (
              <h2 className="mb-6 text-3xl font-bold text-gray-900 md:text-4xl">
                {getText(service.videoHeading)}
              </h2>
            )}
            <div className="mx-auto max-w-3xl">
              <p
                className="text-lg leading-relaxed text-gray-700"
                dangerouslySetInnerHTML={{ __html: getText(service.whatIs) }}
              />
            </div>
          </section>
        )}
      </div>

      {getText(service.benefits) && (
        <section className="mb-16">
          {benefitsBackgroundImage && (
            <div
              className="relative h-96 w-full md:h-[500px]"
              style={{
                backgroundImage: `url(${getAssetUrl(benefitsBackgroundImage)})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }}
            >
              <div className="absolute inset-0 bg-black/50"></div>
              <div className="relative z-10 flex h-full flex-col justify-center">
                <div className="container mx-auto max-w-6xl px-4 md:px-4">
                  <div className="max-w-2xl pl-4 md:pl-0">
                    {getText(service.benefitsHeading) && (
                      <h2 className="mb-6 text-3xl font-bold text-white md:text-4xl">
                        {getText(service.benefitsHeading)}
                      </h2>
                    )}
                    <ul
                      className="space-y-3 text-white [&>li>p]:mb-0 [&>li]:flex [&>li]:items-start [&>li]:space-x-3 [&>li]:leading-relaxed"
                      dangerouslySetInnerHTML={{
                        __html: getText(service.benefits)
                          .replace(/<li/g, '<li class="flex items-start space-x-3"')
                          .replace(
                            /<li class="flex items-start space-x-3"/g,
                            '<li class="flex items-start space-x-3"><span class="flex-shrink-0 w-2 h-2 bg-white rounded-full mt-2"></span><div'
                          )
                          .replace(/<\/li>/g, '</div></li>'),
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
          {!benefitsBackgroundImage && (
            <div className="container mx-auto max-w-6xl px-4">
              <div className="rounded-2xl bg-gradient-to-br from-blue-50 to-indigo-50 p-8 shadow-lg md:p-12">
                {getText(service.benefitsHeading) && (
                  <h2 className="mb-6 text-3xl font-bold text-gray-900 md:text-4xl">
                    {getText(service.benefitsHeading)}
                  </h2>
                )}
                <ul
                  className="space-y-3 text-gray-700 [&>li>p]:mb-0 [&>li]:flex [&>li]:items-start [&>li]:space-x-3"
                  dangerouslySetInnerHTML={{
                    __html: getText(service.benefits)
                      .replace(/<li/g, '<li class="flex items-start space-x-3"')
                      .replace(
                        /<li class="flex items-start space-x-3"/g,
                        '<li class="flex items-start space-x-3"><span class="flex-shrink-0 w-2 h-2 bg-blue-600 rounded-full mt-2"></span><div'
                      )
                      .replace(/<\/li>/g, '</div></li>'),
                  }}
                />
              </div>
            </div>
          )}
        </section>
      )}

      <div className="container mx-auto max-w-6xl px-4 py-12">
        {getText(service.howItWorksBody) && (
          <section className="mb-16">
            <div className="flex items-start gap-8">
              {/* How it Works Icon/Image */}
              <div className="hidden flex-shrink-0 md:block">
                {howItWorksImage ? (
                  <img
                    src={getAssetUrl(howItWorksImage)}
                    alt={serviceName}
                    className="h-[120px] w-[80px] object-contain"
                  />
                ) : (
                  <div className="flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-blue-100 to-blue-200">
                    <span className="text-sm font-bold text-blue-600">Icon</span>
                  </div>
                )}
              </div>
              {/* How it Works Text */}
              <div className="flex-1 space-y-6">
                {getText(service.howItWorksHeading) && (
                  <h2 className="text-3xl font-bold text-gray-900 md:text-4xl">
                    {getText(service.howItWorksHeading)}
                  </h2>
                )}
                <div
                  className="prose prose-lg max-w-none leading-relaxed text-gray-700"
                  dangerouslySetInnerHTML={{ __html: getText(service.howItWorksBody) }}
                />
                {/* Colored line separator - blue, wider, and centered */}
                <div className="mx-auto mt-12 h-0.5 w-3/5 bg-blue-800"></div>
              </div>
            </div>
          </section>
        )}

        {getText(service.experienceBody) && (
          <section className="mb-16">
            {/* Experience Image in its own row */}
            {experienceImage && (
              <div className="mb-8 flex justify-center">
                <img
                  src={getAssetUrl(experienceImage)}
                  alt={serviceName}
                  className="h-auto w-full object-cover md:w-5/6 md:rounded-2xl"
                />
              </div>
            )}

            {/* Content Block with light background color */}
            <div
              className="rounded-2xl p-8 md:p-12"
              style={{ backgroundColor: 'rgba(229, 240, 254, 0.8)' }}
            >
              {getText(service.experienceHeading) && (
                <h2 className="mb-8 text-3xl font-bold text-gray-900 md:text-4xl">
                  {getText(service.experienceHeading)}
                </h2>
              )}
              <ul className="space-y-4 text-gray-700">
                {getText(service.experienceBody)
                  .split('<li>')
                  .filter((item) => item.trim())
                  .map((item, index) => {
                    const cleanItem = item
                      .replace(/<\/li>.*/, '')
                      .replace(/<[^>]*>/g, '')
                      .trim();
                    if (!cleanItem) return null;
                    return (
                      <li key={index} className="flex items-start space-x-3">
                        <span className="mt-2 h-2 w-2 flex-shrink-0 rounded-full bg-blue-600"></span>
                        <span className="leading-relaxed">{cleanItem}</span>
                      </li>
                    );
                  })
                  .filter(Boolean)}
              </ul>
            </div>
          </section>
        )}

        <section className="py-12 text-center">
          <LocalizedButton to={`/book/${service.id}`} name={t.servicePage?.bookNow} />
        </section>
      </div>
    </div>
  );
};

export default ServicePage;
